import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Wallet, TrendingUp, ShoppingBag, AlertTriangle, Search, Boxes, Truck, Undo2, Download } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { KpiCard } from '@/components/ui/KpiCard';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { Table } from '@/components/ui/Table';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { formatINR, formatDate } from '@/lib/format';
import { getOnlineProfitability, saveOnlineOrderCosts, onlineProfitabilityExportPath, type ProfitRow } from '@/api/onlineProfitability';
import { downloadExcel } from '@/lib/downloadExcel';
import { PeriodPicker, REPORT_UNITS, usePeriod } from '@/components/ui/PeriodPicker';

type Cost = { freightCost: string; returnFreightCost: string; otherCost: string; remark: string };
const money = (n: number) => `₹${formatINR(n)}`;

export function OnlineProfitabilityPage() {
  const toast = useToast();
  const period = usePeriod();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'' | 'processed' | 'pending'>('');
  const [edits, setEdits] = useState<Record<number, Cost>>({});

  const { range } = period;
  const filter = useMemo(() => ({ from: range?.from, to: range?.to, search: search || undefined, status: status || undefined }), [range, search, status]);
  const q = useQuery({ queryKey: ['online-profitability', filter], queryFn: () => getOnlineProfitability(filter) });
  const rows = q.data?.rows ?? [];

  // Seed local editable costs whenever data loads.
  useEffect(() => {
    const m: Record<number, Cost> = {};
    for (const r of rows) m[r.orderId] = { freightCost: String(r.freightCost), returnFreightCost: String(r.returnFreightCost), otherCost: String(r.otherCost), remark: r.remark ?? '' };
    setEdits(m);
  }, [q.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveMut = useMutation({
    mutationFn: ({ orderId, c }: { orderId: number; c: Cost }) =>
      saveOnlineOrderCosts(orderId, { freightCost: Number(c.freightCost || 0), returnFreightCost: Number(c.returnFreightCost || 0), otherCost: Number(c.otherCost || 0), remark: c.remark }),
    onSuccess: () => toast.push('Saved', 'success'),
    onError: (e) => toast.push(apiError(e, 'Save failed'), 'error'),
  });

  // Export every matching order (server-side, no cap) — the table above stops at 1000.
  function onExport() {
    downloadExcel(onlineProfitabilityExportPath(filter), 'GRHAMOY_Online_Profitability.xlsx', 120_000)
      .catch((err) => toast.push(apiError(err, 'Export failed'), 'error'));
  }

  const setCost = (orderId: number, key: keyof Cost, v: string) => setEdits((s) => ({ ...s, [orderId]: { ...s[orderId], [key]: v } }));
  const grossOf = (r: ProfitRow) => {
    const c = edits[r.orderId];
    if (!c) return r.grossProfit;
    return Math.round((r.valueWithoutGst - r.totalCosting - Number(c.freightCost || 0) - Number(c.returnFreightCost || 0) - Number(c.otherCost || 0)) * 100) / 100;
  };
  const totalGross = rows.reduce((a, r) => a + grossOf(r), 0);
  // Freight totals follow the editable cells, so the cards stay in step with Gross Profit while typing.
  const totalFreight = rows.reduce((a, r) => a + Number(edits[r.orderId]?.freightCost ?? r.freightCost ?? 0), 0);
  const totalReturnFreight = rows.reduce((a, r) => a + Number(edits[r.orderId]?.returnFreightCost ?? r.returnFreightCost ?? 0), 0);

  const costInput = (r: ProfitRow, key: keyof Cost) => (
    <input
      type="number"
      min={0}
      value={edits[r.orderId]?.[key] ?? ''}
      onChange={(e) => setCost(r.orderId, key, e.target.value)}
      onBlur={() => edits[r.orderId] && saveMut.mutate({ orderId: r.orderId, c: edits[r.orderId] })}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
      className="w-20 rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] px-1.5 py-0.5 text-right text-xs outline-none focus:border-accent"
      placeholder="0"
    />
  );

  const remarkInput = (r: ProfitRow) => (
    <input
      type="text"
      maxLength={500}
      value={edits[r.orderId]?.remark ?? ''}
      onChange={(e) => setCost(r.orderId, 'remark', e.target.value)}
      onBlur={() => edits[r.orderId] && (edits[r.orderId].remark ?? '') !== (r.remark ?? '') && saveMut.mutate({ orderId: r.orderId, c: edits[r.orderId] })}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
      title={edits[r.orderId]?.remark || 'Add a remark'}
      className="w-48 rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] px-1.5 py-0.5 text-xs outline-none focus:border-accent"
      placeholder="Add remark"
    />
  );

  return (
    <div>
      <SectionHeader title="Online Profitability" subtitle={range ? `Orders placed ${range.label}` : undefined} />

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        <KpiCard label="Orders" value={String(rows.length)} accent="#6366f1" Icon={ShoppingBag} />
        <KpiCard label="Order Value (ex-GST)" value={money(q.data?.totals.valueWithoutGst ?? 0)} accent="#0ea5e9" Icon={Wallet} />
        <KpiCard label="Product Costing" value={money(q.data?.totals.totalCosting ?? 0)} accent="#f59e0b" Icon={Boxes} />
        <KpiCard label="Freight Cost" value={money(totalFreight)} accent="#8b5cf6" Icon={Truck} />
        <KpiCard label="Return Freight" value={money(totalReturnFreight)} accent="#ef4444" Icon={Undo2} />
        <KpiCard label="Gross Profit" value={money(totalGross)} accent="#10b981" Icon={TrendingUp} />
      </div>

      <FilterBar>
        <FilterField label="Search" className="col-span-2 sm:col-auto sm:min-w-[220px]">
          <div className="relative w-full" title="Search by order ID or customer">
            <Search
              size={11}
              className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5"
              strokeWidth={2.25}
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              className="!pl-5 sm:!pl-8 sm:min-w-[260px]"
              aria-label="Search online orders"
            />
          </div>
        </FilterField>
        <FilterField label="Orders" className="sm:w-[230px]">
          <Select value={status} onChange={(e) => setStatus(e.target.value as '' | 'processed' | 'pending')}>
            <option value="">All</option>
            <option value="processed">Processed in ERP</option>
            <option value="pending">Pending</option>
          </Select>
        </FilterField>
        <FilterField label="Period" className="col-span-3 sm:col-auto">
          <PeriodPicker {...period.picker} units={REPORT_UNITS} className="justify-start" />
        </FilterField>
        <button
          type="button"
          onClick={onExport}
          className="col-span-2 inline-flex h-7 items-center justify-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-accent hover:text-accent sm:col-span-1 sm:ml-auto sm:h-8 sm:self-end sm:px-3 sm:text-xs"
          title="Export every order matching these filters to Excel"
        >
          <Download size={12} /> Export
        </button>
      </FilterBar>

      <Table<ProfitRow>
        storageKey="online-profitability"
        paginated
        title="Online Orders"
        count={`${rows.length} order(s) · Gross profit ₹${formatINR(totalGross)}`}
        rows={rows}
        rowKey={(r) => String(r.orderId)}
        loading={q.isFetching && rows.length === 0}
        empty="No online orders for this filter. Shopify orders appear here once pulled."
        columns={[
          { key: 'date', header: 'Order Date', minWidth: '100px', cell: (r) => (r.orderDate ? formatDate(r.orderDate) : '—') },
          { key: 'id', header: 'Order ID', minWidth: '110px', cell: (r) => <span className="font-extrabold text-accent">{r.orderName}</span> },
          { key: 'vg', header: 'Order Value with GST', align: 'right', minWidth: '120px', cell: (r) => money(r.valueWithGst) },
          { key: 'vng', header: 'Order Value without GST', align: 'right', minWidth: '130px', cell: (r) => money(r.valueWithoutGst) },
          {
            key: 'cost', header: 'Product Costing (Item Master)', align: 'right', minWidth: '140px',
            cell: (r) => <span>{money(r.totalCosting)}{r.unmappedLines > 0 && <span className="ml-1 text-[10px] text-warning" title="Some lines have no mapped item — costing is partial"><AlertTriangle size={10} className="inline-block align-[-1px]" />{r.unmappedLines}</span>}</span>,
          },
          { key: 'freight', header: 'Freight (courier)', align: 'right', minWidth: '110px', cell: (r) => costInput(r, 'freightCost') },
          { key: 'retfreight', header: 'Return Freight', align: 'right', minWidth: '110px', cell: (r) => costInput(r, 'returnFreightCost') },
          { key: 'other', header: 'Any other cost', align: 'right', minWidth: '110px', cell: (r) => costInput(r, 'otherCost') },
          { key: 'gp', header: 'Gross Profit', align: 'right', minWidth: '110px', cell: (r) => { const g = grossOf(r); return <strong className={g >= 0 ? 'text-success' : 'text-danger'}>{money(g)}</strong>; } },
          { key: 'remark', header: 'Remark', minWidth: '200px', cell: (r) => remarkInput(r) },
        ]}
      />
    </div>
  );
}
